import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Eye, EyeOff, Loader2, AlertCircle, TreePine, Copy } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  const [tempCredentials, setTempCredentials] = useState(null);
  const [accounts, setAccounts] = useState(null);
  // Which account was chosen on the account-selector screen, if any — must
  // be threaded through to /auth/login-2fa so it verifies the TOTP against
  // THIS specific account rather than an arbitrary one sharing the same
  // email (see routers/auth.py::login_with_2fa).
  const [selectedAccountId, setSelectedAccountId] = useState(null);

  // Forced first-time MFA enrollment (account has require_mfa set and has
  // never enrolled). Distinct from requires2FA above, which is for accounts
  // that already have TOTP enabled and just need a code.
  const [requiresMfaSetup, setRequiresMfaSetup] = useState(false);
  const [mfaSetupToken, setMfaSetupToken] = useState(null);
  const [mfaPendingUser, setMfaPendingUser] = useState(null);
  const [mfaQr, setMfaQr] = useState(null);
  const [mfaSecret, setMfaSecret] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaBackupCodes, setMfaBackupCodes] = useState(null);

  const { login, loginWith2FA, completeMfaSetupLogin, selectAccount } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/dashboard';

  const navigateByRole = (role) => {
    // Mirrors RoleBasedRedirect in App.js — keep the two in sync.
    if (role === 'client') navigate('/client', { replace: true });
    else if (role === 'admin' || role === 'analyst' || role === 'operations') navigate('/admin', { replace: true });
    else if (role === 'compliance') navigate('/compliance', { replace: true });
    else if (role === 'investor_relations') navigate('/ir', { replace: true });
    else navigate('/dashboard', { replace: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password);

      if (result.requires_account_selection && result.accounts?.length > 0) {
        setAccounts(result.accounts);
        setTempCredentials({ email, password });
      } else if (result.requires_2fa) {
        setRequires2FA(true);
        setTempCredentials({ email, password });
      } else if (result.requires_mfa_setup) {
        await startForcedMfaSetup(result.mfa_setup_token, result.user);
      } else {
        navigateByRole(result.user?.role);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  // This account is required to enroll in 2FA and hasn't yet (routers/auth.py
  // returned requires_mfa_setup + a scoped mfa_setup_token instead of a full
  // session). Fetch the QR immediately so the user lands straight on the
  // enrollment screen.
  const startForcedMfaSetup = async (setupToken, user) => {
    setMfaSetupToken(setupToken);
    setMfaPendingUser(user);
    setRequiresMfaSetup(true);
    try {
      const r = await axios.post(`${API_URL}/api/auth/2fa/setup`, {}, {
        headers: { Authorization: `Bearer ${setupToken}` },
      });
      setMfaQr(`data:image/png;base64,${r.data.qr_png_base64}`);
      setMfaSecret(r.data.secret_plaintext);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not start two-factor setup');
    }
  };

  const handleMfaSetupSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/auth/2fa/verify-setup`,
        { code: mfaCode },
        { headers: { Authorization: `Bearer ${mfaSetupToken}` } }
      );
      setMfaBackupCodes(r.data.backup_codes || []);
      // The backend mints a real session as part of this response since it
      // knows this was a mandatory-enrollment (mfa_setup_only) token — store
      // it now, but keep showing the backup codes until acknowledged.
      if (r.data.access_token) {
        completeMfaSetupLogin(r.data.access_token, r.data.user);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid code');
    } finally {
      setLoading(false);
    }
  };

  const handleAccountSelect = async (accountId) => {
    setError('');
    setLoading(true);
    try {
      const result = await selectAccount(tempCredentials.email, tempCredentials.password, accountId);
      if (result.requires_2fa) {
        setSelectedAccountId(accountId);
        setRequires2FA(true);
        setAccounts(null);
      } else if (result.requires_mfa_setup) {
        setAccounts(null);
        await startForcedMfaSetup(result.mfa_setup_token, result.user);
      } else {
        navigateByRole(result.user?.role);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to select account');
    } finally {
      setLoading(false);
    }
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await loginWith2FA(tempCredentials.email, tempCredentials.password, totpCode, selectedAccountId);
      navigateByRole(result.user?.role);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid authentication code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Branded panel */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-[#1D2D2A]">
        {/* Subtle pattern overlay */}
        <div className="absolute inset-0 opacity-10" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23BC9D6E\' fill-opacity=\'0.4\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'}}></div>
        
        <div className="relative z-10 w-full flex flex-col justify-center items-center p-12">
          <div className="max-w-md text-center">
            {/* Logo */}
            <img 
              src="/logo-tree.svg" 
              alt="Plane Tree Capital" 
              className="h-32 mx-auto mb-8"
            />
            
            {/* Title */}
            <h1 className="text-3xl font-bold text-[#DFD5BC] font-['Manrope'] mb-6">
              Plane Tree Capital Portal
            </h1>
            
            {/* Decorative line */}
            <div className="flex items-center justify-center gap-4">
              <div className="h-px w-16 bg-[#BC9D6E]/40"></div>
              <div className="w-2 h-2 rounded-full bg-[#BC9D6E]"></div>
              <div className="h-px w-16 bg-[#BC9D6E]/40"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-[#F8F9FA]">
        <div className="w-full max-w-md animate-fade-in">
          {/* Mobile logo - Tree logo for better visibility */}
          <div className="lg:hidden flex flex-col items-center justify-center mb-8">
            <img 
              src="/logo-tree.svg" 
              alt="Plane Tree Capital" 
              className="h-20 mb-3"
            />
            <span className="text-lg font-semibold text-[#1D2D2A] font-['Manrope']">Plane Tree Capital</span>
          </div>

          <Card className="border-0 shadow-lg">
            <CardHeader className="space-y-1 pb-6">
              <CardTitle className="text-2xl font-bold text-[#212529] font-['Manrope']">
                {requiresMfaSetup ? 'Set Up Two-Factor Authentication' : requires2FA ? 'Two-Factor Authentication' : 'Welcome back'}
              </CardTitle>
              <CardDescription className="text-[#6C757D]">
                {requiresMfaSetup
                  ? 'Your administrator requires two-factor authentication on this account before you can continue.'
                  : requires2FA
                  ? 'Enter the code from your authenticator app'
                  : 'Sign in to access your investor portal'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {error && (
                <Alert variant="destructive" className="mb-6 bg-red-50 border-red-200">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {requiresMfaSetup ? (
                <div className="space-y-5" data-testid="mfa-setup-required-panel">
                  {mfaBackupCodes ? (
                    <div className="space-y-4">
                      <div className="p-3 rounded border-2 border-emerald-500 bg-emerald-50">
                        <p className="text-sm font-semibold text-emerald-800">Two-factor authentication is now active</p>
                        <p className="text-xs text-emerald-700 mt-1">
                          Save these backup codes somewhere safe — this is the only time they will be shown. Each works once.
                        </p>
                      </div>
                      <div className="grid grid-cols-2 gap-2 font-mono text-sm bg-slate-50 p-3 rounded border">
                        {mfaBackupCodes.map((c, i) => <code key={i} className="text-center py-1">{c}</code>)}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          className="flex-1"
                          onClick={() => { navigator.clipboard.writeText(mfaBackupCodes.join('\n')); }}
                        >
                          <Copy className="h-4 w-4 mr-2" /> Copy all
                        </Button>
                        <Button
                          type="button"
                          className="flex-1 bg-[#1D2D2A] hover:bg-[#243D37] text-white"
                          data-testid="mfa-setup-continue-button"
                          onClick={() => navigateByRole(mfaPendingUser?.role)}
                        >
                          I have saved my codes — continue
                        </Button>
                      </div>
                    </div>
                  ) : mfaQr ? (
                    <form onSubmit={handleMfaSetupSubmit} className="space-y-4">
                      <p className="text-sm text-[#6C757D]">
                        Scan this QR code with an authenticator app (Google Authenticator, Authy, 1Password, etc.).
                      </p>
                      <img src={mfaQr} alt="Two-factor QR code" className="w-48 h-48 border rounded mx-auto" />
                      <div className="text-xs text-[#6C757D] text-center">
                        Or enter this secret manually:
                        <code className="ml-2 bg-slate-100 px-2 py-1 rounded font-mono text-sm">{mfaSecret}</code>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="mfa-setup-code" className="text-[#212529] font-medium">6-digit code</Label>
                        <Input
                          id="mfa-setup-code"
                          type="text"
                          placeholder="Enter 6-digit code"
                          value={mfaCode}
                          onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          required
                          maxLength={6}
                          data-testid="mfa-setup-code-input"
                          className="h-11 text-center text-xl tracking-widest border-gray-200 focus:border-[#1D2D2A] focus:ring-[#1D2D2A]"
                        />
                      </div>
                      <Button
                        type="submit"
                        disabled={loading || mfaCode.length !== 6}
                        data-testid="mfa-setup-verify-button"
                        className="w-full h-11 bg-[#1D2D2A] hover:bg-[#243D37] text-white"
                      >
                        {loading ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Verifying...</>) : 'Verify & activate'}
                      </Button>
                    </form>
                  ) : (
                    <div className="flex items-center justify-center py-8 text-[#6C757D]">
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Preparing setup...
                    </div>
                  )}
                </div>
              ) : !requires2FA && !accounts ? (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-[#212529] font-medium">
                      Email address
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      data-testid="login-email-input"
                      className="h-11 border-gray-200 focus:border-[#1D2D2A] focus:ring-[#1D2D2A]"
                    />
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="password" className="text-[#212529] font-medium">
                        Password
                      </Label>
                      <Link
                        to="/forgot-password"
                        className="text-sm text-[#18A37C] hover:text-[#1D2D2A] transition-colors"
                        data-testid="forgot-password-link"
                      >
                        Forgot password?
                      </Link>
                    </div>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Enter your password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        data-testid="login-password-input"
                        className="h-11 pr-10 border-gray-200 focus:border-[#1D2D2A] focus:ring-[#1D2D2A]"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        data-testid="toggle-password-visibility"
                      >
                        {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    data-testid="login-submit-button"
                    className="w-full h-11 bg-[#1D2D2A] hover:bg-[#243D37] text-white font-medium transition-all duration-200 active:scale-[0.98]"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Signing in...
                      </>
                    ) : (
                      'Sign in'
                    )}
                  </Button>
                </form>
              ) : accounts ? (
                /* Account Selector — shown when email maps to multiple accounts */
                <div className="space-y-4" data-testid="account-selector">
                  <p className="text-sm text-[#6C757D]">
                    Select which account to access:
                  </p>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {accounts.map((acct) => (
                      <button
                        key={acct.id}
                        onClick={() => handleAccountSelect(acct.id)}
                        disabled={loading}
                        data-testid={`account-option-${acct.reference_id || acct.risk_participant_code || acct.id}`}
                        className="w-full flex items-center justify-between p-3 rounded-lg border border-gray-200 hover:border-[#1D2D2A] hover:bg-[#F0F5F3] transition-all text-left"
                      >
                        <div>
                          <p className="font-medium text-[#212529]">
                            {acct.full_name || `${acct.first_name} ${acct.last_name}`}
                          </p>
                          {acct.entity_name && (
                            <p className="text-xs text-[#6C757D]">{acct.entity_name}</p>
                          )}
                          <p className="text-sm text-[#6C757D] font-mono">
                            {acct.risk_participant_code ? `RP: ${acct.risk_participant_code}` : acct.reference_id || acct.role}
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <span className="text-xs px-2 py-1 rounded bg-[#E8F5F0] text-[#1D2D2A] font-medium capitalize">
                            {acct.role}
                          </span>
                          {acct.is_managed && (
                            <span className="text-xs px-2 py-0.5 rounded bg-amber-50 text-amber-700 font-medium">
                              Managed
                            </span>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => { setAccounts(null); setTempCredentials(null); }}
                    className="w-full h-11 border-gray-200"
                    data-testid="back-from-account-select"
                  >
                    Back to login
                  </Button>
                </div>
              ) : (
                <form onSubmit={handle2FASubmit} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="totp" className="text-[#212529] font-medium">
                      Authentication Code
                    </Label>
                    <Input
                      id="totp"
                      type="text"
                      placeholder="Enter 6-digit code"
                      value={totpCode}
                      onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      required
                      maxLength={6}
                      data-testid="totp-code-input"
                      className="h-11 text-center text-xl tracking-widest border-gray-200 focus:border-[#1D2D2A] focus:ring-[#1D2D2A]"
                    />
                    <p className="text-xs text-[#6C757D] mt-1">
                      Open your authenticator app to view your code
                    </p>
                  </div>

                  <div className="flex gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setRequires2FA(false);
                        setTotpCode('');
                        setTempCredentials(null);
                        setSelectedAccountId(null);
                      }}
                      className="flex-1 h-11 border-gray-200"
                      data-testid="back-to-login-button"
                    >
                      Back
                    </Button>
                    <Button
                      type="submit"
                      disabled={loading || totpCode.length !== 6}
                      data-testid="verify-2fa-button"
                      className="flex-1 h-11 bg-[#1D2D2A] hover:bg-[#243D37] text-white"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Verifying...
                        </>
                      ) : (
                        'Verify'
                      )}
                    </Button>
                  </div>
                </form>
              )}

              <div className="mt-6 text-center">
                <p className="text-sm text-[#6C757D]">
                  New investor?{' '}
                  <span className="text-[#18A37C]">
                    Contact your relationship manager for access
                  </span>
                </p>
                <p className="text-sm text-[#6C757D] mt-2">
                  <a 
                    href="https://plane-tree.com" 
                    className="text-[#18A37C] hover:text-[#315C52] hover:underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    &larr; Back to main website
                  </a>
                </p>
              </div>
            </CardContent>
          </Card>

          <p className="mt-8 text-center text-xs text-[#ADB5BD]">
            &copy; {new Date().getFullYear()} Plane Tree Capital. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
